import React, { useEffect, useContext, useState, useRef, useCallback } from 'react'
import styles from './Sidebar.module.css'
import { getCourses, getHistory } from '@/api'
import { UserContext } from '../../contexts/UserContext'
import CoursesList from '../CoursesList/CoursesList'
import { SidebarContext } from '../../contexts/SidebarContextProps'
import { Course } from '@/models'

const MIN_SIDEBAR_WIDTH = 160
const MAX_SIDEBAR_WIDTH = 480
const DEFAULT_SIDEBAR_WIDTH = 224 // 14rem
const SIDEBAR_WIDTH_STORAGE_KEY = 'sidebarWidth'

const Sidebar = () => {
    const userContext = useContext(UserContext)
    const isCollapsed = useContext(SidebarContext).isCollapsed
    const sidebarContext = useContext(SidebarContext)

    const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
        const stored = Number(localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY))
        if (stored >= MIN_SIDEBAR_WIDTH && stored <= MAX_SIDEBAR_WIDTH) {
            return stored
        }
        return DEFAULT_SIDEBAR_WIDTH
    })
    const [isResizing, setIsResizing] = useState(false)
    const sidebarRef = useRef<HTMLDivElement | null>(null)

    const handleResizeMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        setIsResizing(true)
    }, [])

    useEffect(() => {
        if (!isResizing) return

        const handleMouseMove = (e: MouseEvent) => {
            const left = sidebarRef.current?.getBoundingClientRect().left ?? 0
            const newWidth = Math.min(
                MAX_SIDEBAR_WIDTH,
                Math.max(MIN_SIDEBAR_WIDTH, e.clientX - left),
            )
            setSidebarWidth(newWidth)
        }

        const handleMouseUp = () => {
            setIsResizing(false)
        }

        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'
        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)

        return () => {
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [isResizing])

    useEffect(() => {
        localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(sidebarWidth))
    }, [sidebarWidth])

    useEffect(() => {
        const fetchData = async () => {
            sidebarContext.setIsLoadingCourses(true)
            userContext.checkExpiry()
            if (!userContext.idToken || !userContext.userId) {
                sidebarContext.setIsLoadingCourses(false)
                return
            }
            try {
                const coursesData = await getCourses(userContext.idToken, userContext.accessToken, userContext.userId)
                if (Object.keys(coursesData).length === 0) {
                    console.error('The user is not enrolled in any courses')
                    alert(
                        'You are not enrolled in any courses that are part of MyTutor. Please contact your system administrator to get enrolled in a course.',
                    )
                    sidebarContext.setCourses([])
                    sidebarContext.setIsLoadingCourses(false)
                    return
                }

                let updatedCourses = [] as Course[]

                try {
                    const historyData = await getHistory(userContext.userId, userContext.idToken)
                    if (Object.keys(historyData).length === 0) {
                        console.log('No history found for the user')
                    }
                    updatedCourses = coursesData.courses.map((course) => {
                        return {
                            name: course.name,
                            welcomeMessages: course.welcomeMessages,
                            history: historyData[course.name]
                                ? historyData[course.name].map((history) => ({
                                      conversation_id: history.conversation_id,
                                      course: course.name,
                                      title: history.title,
                                      updated_at: history.updated_at,
                                  }))
                                : [],
                        }
                    })
                    sidebarContext.setCourses(updatedCourses)
                    sidebarContext.setIsLoadingCourses(false)
                } catch (e) {
                    alert(
                        'An error occurred while fetching courses. Please try again later. If the problem persists, contact your system administrator.',
                    )
                    console.error(`Error in fetching history: ${e}`)
                    sidebarContext.setCourses(updatedCourses)
                    sidebarContext.setIsLoadingCourses(false)
                }
            } catch (e) {
                console.error(`Error in fetching courses: ${e}`)
                sidebarContext.setIsLoadingCourses(false)
            }
        }
        fetchData()
    }, [userContext.userId])
    let content
    if (sidebarContext.isLoadingCourses) {
        content = (
            <div className={styles.sidebarTitle}>
                <p>Loading Courses</p>
            </div>
        )
    } else if (!sidebarContext.courses || sidebarContext.courses.length === 0) {
        content = (
            <div className={styles.sidebarTitle}>
                <p>No courses found!</p>
            </div>
        )
    } else {
        content = (
            <>
                <CoursesList />
            </>
        )
    }
    return (
        <div
            ref={sidebarRef}
            className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ''} ${isResizing ? styles.resizing : ''}`}
            style={{ width: isCollapsed ? 0 : sidebarWidth }}
        >
            <div className={styles.sidebarTitle}>
                <span>Available Courses</span>
            </div>
            {content}
            {!isCollapsed && (
                <div
                    className={styles.resizeHandle}
                    onMouseDown={handleResizeMouseDown}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize courses panel"
                />
            )}
        </div>
    )
}

export default Sidebar
